import React, { useEffect, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';
import { QRCodeSVG } from 'qrcode.react';
import {
  buildProductPublicUrl,
  fetchStoreProductPublicBaseUrl,
} from './productPublicUrl';

function isEan13(value) {
  return /^\d{13}$/.test(String(value || '').trim());
}

function printSection(sectionId) {
  document.body.setAttribute('data-inventory-print', sectionId);
  const cleanup = () => {
    document.body.removeAttribute('data-inventory-print');
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  window.print();
  // Fallback if afterprint does not fire
  setTimeout(cleanup, 1000);
}

/**
 * Same-row image, barcode, and QR cards — each with its own print link.
 * QR encodes the product page URL using the store Website from the DB.
 */
const InventoryBarcodePreview = ({
  barcode,
  storeId = null,
  inventoryItemId = null,
  itemName = '',
  priceLabel = '',
  imageUrl = '',
  fallbackImage = '',
  isGroceryPlaceholder = false,
}) => {
  const svgRef = useRef(null);
  const [renderError, setRenderError] = useState('');
  const [productUrl, setProductUrl] = useState('');
  const [websiteError, setWebsiteError] = useState('');
  const [imgClass, setImgClass] = useState(
    `inventory-detail-item-image${isGroceryPlaceholder ? ' inventory-grocery-placeholder' : ''}`
  );
  const code = String(barcode || '').trim();

  useEffect(() => {
    setRenderError('');
    if (!code || !svgRef.current) return undefined;

    try {
      const options = {
        displayValue: true,
        fontSize: 14,
        textMargin: 4,
        margin: 8,
        height: 64,
        width: 2,
        background: '#ffffff',
        lineColor: '#111111',
      };
      if (isEan13(code)) {
        JsBarcode(svgRef.current, code, { ...options, format: 'EAN13' });
      } else {
        JsBarcode(svgRef.current, code, { ...options, format: 'CODE128' });
      }
    } catch (err) {
      console.error('Barcode render failed:', err);
      setRenderError('Could not render barcode for this value.');
      if (svgRef.current) {
        svgRef.current.innerHTML = '';
      }
    }
    return undefined;
  }, [code]);

  useEffect(() => {
    let cancelled = false;
    setWebsiteError('');
    setProductUrl('');

    const id = inventoryItemId;
    const name = String(itemName || '').trim();
    if (storeId == null || storeId === '' || id == null || !name) {
      return undefined;
    }

    (async () => {
      try {
        const baseUrl = await fetchStoreProductPublicBaseUrl(storeId);
        if (cancelled) return;
        if (!baseUrl) {
          setWebsiteError(
            'Add Website under this store’s business profile → Media (social links) to generate product QR codes.'
          );
          return;
        }
        const url = buildProductPublicUrl(
          { inventoryItemId: id, itemName: name },
          { baseUrl }
        );
        if (!cancelled) setProductUrl(url);
      } catch (err) {
        console.error('Failed to load store website for QR:', err);
        if (!cancelled) {
          setWebsiteError('Could not load store website for product QR.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storeId, inventoryItemId, itemName]);

  const qrValue = productUrl;

  return (
    <div className="inventory-media-codes-row">
      <div className="inventory-media-card">
        <div className="inventory-media-card__header">
          <span className="inventory-media-card__label">Image</span>
          <a
            href="#print-image"
            className="inventory-barcode-print-link"
            onClick={(e) => {
              e.preventDefault();
              printSection('inventory-print-image');
            }}
          >
            Print
          </a>
        </div>
        <div
          className="inventory-media-card__body inventory-media-card__body--image"
          id="inventory-print-image"
        >
          {itemName ? (
            <div className="inventory-barcode-label-sheet__name">{itemName}</div>
          ) : null}
          {priceLabel ? (
            <div className="inventory-barcode-label-sheet__price">{priceLabel}</div>
          ) : null}
          <img
            src={imageUrl || fallbackImage}
            alt={itemName || 'Item'}
            className={imgClass}
            onError={(e) => {
              e.target.onerror = null;
              if (fallbackImage) e.target.src = fallbackImage;
              if (isGroceryPlaceholder || !imageUrl) {
                setImgClass('inventory-detail-item-image inventory-grocery-placeholder');
              }
            }}
          />
        </div>
      </div>

      <div className="inventory-media-card">
        <div className="inventory-media-card__header">
          <span className="inventory-media-card__label">Barcode</span>
          {code ? (
            <a
              href="#print-barcode"
              className="inventory-barcode-print-link"
              onClick={(e) => {
                e.preventDefault();
                if (!renderError) printSection('inventory-print-barcode');
              }}
            >
              Print
            </a>
          ) : null}
        </div>
        <div
          className="inventory-media-card__body inventory-media-card__body--barcode"
          id="inventory-print-barcode"
        >
          {itemName ? (
            <div className="inventory-barcode-label-sheet__name">{itemName}</div>
          ) : null}
          {priceLabel ? (
            <div className="inventory-barcode-label-sheet__price">{priceLabel}</div>
          ) : null}
          {!code ? (
            <span className="restaurant-detail-value">—</span>
          ) : renderError ? (
            <p className="inventory-barcode-preview__error">{renderError}</p>
          ) : (
            <svg
              ref={svgRef}
              className="inventory-barcode-label-sheet__svg"
              role="img"
              aria-label={`Barcode ${code}`}
            />
          )}
        </div>
      </div>

      <div className="inventory-media-card">
        <div className="inventory-media-card__header">
          <span className="inventory-media-card__label">QR code</span>
          {qrValue ? (
            <a
              href="#print-qr"
              className="inventory-barcode-print-link"
              onClick={(e) => {
                e.preventDefault();
                printSection('inventory-print-qr');
              }}
            >
              Print
            </a>
          ) : null}
        </div>
        <div
          className="inventory-media-card__body inventory-media-card__body--qr"
          id="inventory-print-qr"
        >
          {itemName ? (
            <div className="inventory-barcode-label-sheet__name">{itemName}</div>
          ) : null}
          {priceLabel ? (
            <div className="inventory-barcode-label-sheet__price">{priceLabel}</div>
          ) : null}
          {websiteError ? (
            <p className="inventory-barcode-preview__error">{websiteError}</p>
          ) : !qrValue ? (
            <span className="restaurant-detail-value">—</span>
          ) : (
            <div
              className="inventory-barcode-label-sheet__qr"
              aria-label={`QR code ${qrValue}`}
              title={qrValue}
            >
              <QRCodeSVG value={qrValue} size={112} level="M" includeMargin={false} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryBarcodePreview;
